'use client';

import { useAuth } from '@/context/auth.context';
import { AnswerTemplateVariableHint } from '@/components/answer-templates/AnswerTemplateVariableHint';
import { useClientFeatureControls } from '@/context/client-feature-controls.context';
import { useMarketplaceApi } from '@/context/marketplace.context';
import { useReviewsPageQueryContext } from '@/context/reviews-page.context';
import { AnswerTemplatesControl } from '@/components/answer-templates/AnswerTemplatesControl';
import { useModalAnswerPickedTemplates } from '@/components/modal-answer-picked/use-modal-answer-picked-templates';
import { useRefreshClientAiQuota } from '@/hooks/use-refresh-client-ai-quota';
import {
    isReviewAiAnswerJobCanceledError,
    useCancelableReviewAiAnswerJob,
} from '@/hooks/use-cancelable-review-ai-answer-job';
import { QuotaActionButton } from '@/components/billing/QuotaTopUpButton';
import { UIPriority } from '@/enums/ui-priorities';
import { Marketplace } from '@/enums/marketplace';
import { isRuntimeSupportedMarketplace, RuntimeSupportedMarketplace } from '@/shared/marketplace';
import { QUEUE_JOBS } from '@/constants/queue';
import Button from '@/ui/Button';
import Modal from '@/ui/Modal';
import Rating from '@/ui/Rating';
import Select from '@/ui/Select';
import Textarea from '@/ui/Textarea';
import { FC, useMemo, useState } from 'react';
import { toast } from '@/ui/toast';
import { formatMarketplaceApiErrorMessageFromUnknown } from '@/shared/errors/marketplace-api-error-message';
import { isClientAiQuotaExhaustedAvailability } from '@/shared/client-ai-quota';
import { resolveOperationId } from '@/shared/operation-id';
import { createClientLogger } from '@/client/logging/client-logger';
import { logOfferNotAcceptedMarketplacePolicyWarning } from '@/client/logging/offer-not-accepted-policy-logger';

type ModalAiManualProps = {
    isShown: boolean;
    setIsShown: (val: boolean) => void;
};

const DEFAULT_PRODUCT_TYPE = 'Продукт';
const modalAiManualLogger = createClientLogger({
    scope: 'modal-ai-manual',
});
const RATING_OPTIONS = [
    { name: '1', value: '1' },
    { name: '2', value: '2' },
    { name: '3', value: '3' },
    { name: '4', value: '4' },
    { name: '5', value: '5' },
];
const PLATFORM_OPTIONS: Array<{ name: string; value: RuntimeSupportedMarketplace }> = [
    { name: 'WB', value: Marketplace.Wb },
    { name: 'Ozon', value: Marketplace.Ozon },
];

export const resolveSingleRuntimePlatformFilter = (
    value: string | undefined,
): RuntimeSupportedMarketplace | undefined => {
    if (!value) {
        return undefined;
    }

    const candidates = [
        ...new Set(
            value
                .split(',')
                .map((item) => item.trim())
                .filter((item): item is RuntimeSupportedMarketplace => isRuntimeSupportedMarketplace(item)),
        ),
    ];

    return candidates.length === 1 ? candidates[0] : undefined;
};

const ModalAiManual: FC<ModalAiManualProps> = ({ isShown, setIsShown }) => {
    const { accessToken } = useAuth();
    const { featureAvailability } = useClientFeatureControls();
    const { reviewsApi } = useMarketplaceApi();
    const { filters } = useReviewsPageQueryContext();
    const refreshClientAiQuota = useRefreshClientAiQuota();
    const aiAnswerJob = useCancelableReviewAiAnswerJob({
        accessToken,
        jobName: QUEUE_JOBS.generateReviewAiAnswerManual,
    });

    const [rating, setRating] = useState<string>('');
    const [reviewText, setReviewText] = useState<string>('');
    const [aiText, setAiText] = useState<string>('');
    const [aiErrorText, setAiErrorText] = useState<string | null>(null);
    const [isAiGenerating, setIsAiGenerating] = useState<boolean>(false);
    const [selectedPlatform, setSelectedPlatform] = useState<RuntimeSupportedMarketplace | ''>(
        () => resolveSingleRuntimePlatformFilter(filters.platform) || '',
    );

    const normalizedRating = Number(rating);
    const hasValidRating = Number.isInteger(normalizedRating) && normalizedRating >= 1 && normalizedRating <= 5;

    const platformFilter = useMemo(() => resolveSingleRuntimePlatformFilter(filters.platform), [filters.platform]);
    const aiAnswerAvailability = featureAvailability.aiAnswerGeneration;
    const aiAnswerDisabledReason = !aiAnswerAvailability.enabled ? aiAnswerAvailability.reason : null;
    const isAiAnswerQuotaExhausted = isClientAiQuotaExhaustedAvailability(aiAnswerAvailability);
    const canGenerate = useMemo(() => {
        if (isAiGenerating) return false;
        return aiAnswerAvailability.enabled && hasValidRating && !!reviewText.trim();
    }, [aiAnswerAvailability.enabled, hasValidRating, isAiGenerating, reviewText]);
    const templatesBindings = useModalAnswerPickedTemplates({
        isShown,
        accessToken,
        value: aiText,
        setValue: setAiText,
        onTemplateApplied: () => {
            void aiAnswerJob.cancelActive('template_applied');
        },
    });

    const resetModalState = () => {
        setRating('');
        setReviewText('');
        setAiText('');
        setAiErrorText(null);
        setIsAiGenerating(false);
        setSelectedPlatform(platformFilter || '');
    };

    const closeModal = () => {
        void aiAnswerJob.cancelActive('dialog_closed');
        resetModalState();
        setIsShown(false);
    };

    const generateAiAnswer = async () => {
        if (!aiAnswerAvailability.enabled) {
            setAiErrorText(aiAnswerAvailability.reason);
            return;
        }
        if (!canGenerate) return;

        const preparedReviewText = reviewText.trim();

        setAiErrorText(null);
        setAiText('');
        setIsAiGenerating(true);
        const operationId = resolveOperationId(undefined, Date.now());

        try {
            const generatedAnswer = await aiAnswerJob.run({
                operationId,
                enqueue: (resolvedOperationId, { signal }) =>
                    reviewsApi.generateManualAiAnswer(
                        {
                            reviewText: preparedReviewText,
                            productType: DEFAULT_PRODUCT_TYPE,
                            stars: normalizedRating,
                            ...(selectedPlatform ? { platform: selectedPlatform } : {}),
                            operationId: resolvedOperationId,
                        },
                        {
                            authToken: accessToken,
                            signal,
                        },
                    ),
            });

            setAiText(generatedAnswer);
            await refreshClientAiQuota();
        } catch (error) {
            if (isReviewAiAnswerJobCanceledError(error)) {
                return;
            }

            logOfferNotAcceptedMarketplacePolicyWarning({
                logger: modalAiManualLogger,
                message:
                    '[client.modal-ai-manual.offer-policy] Manual AI answer action blocked before offer acceptance.',
                error,
                context: {
                    action: 'modal_ai_manual.ai_answer.generate',
                    operationId,
                    ...(selectedPlatform ? { platform: selectedPlatform } : {}),
                },
            });
            setAiErrorText(
                formatMarketplaceApiErrorMessageFromUnknown(error) ??
                    'Ошибка сервиса ИИ. Не удалось сгенерировать ответ',
            );
            await refreshClientAiQuota();
        } finally {
            setIsAiGenerating(false);
        }
    };

    const copyAiAnswer = async () => {
        const preparedAnswer = aiText.trim();
        if (!preparedAnswer) return;

        try {
            await navigator.clipboard.writeText(preparedAnswer);
            toast.success('Ответ скопирован');
        } catch {
            toast.error('Не удалось скопировать ответ');
        }
    };

    const onPlatformChange = (value: string | undefined) => {
        void aiAnswerJob.cancelActive('draft_changed');
        setSelectedPlatform(value && isRuntimeSupportedMarketplace(value) ? value : '');
    };

    const onRatingChange = (value: string | undefined) => {
        void aiAnswerJob.cancelActive('draft_changed');
        setRating(value || '');
    };

    const onReviewTextChange = (value: string) => {
        void aiAnswerJob.cancelActive('draft_changed');
        setReviewText(value);
    };

    return (
        <Modal isShown={isShown} setIsShown={closeModal} title="Диалог" closable>
            <div className="w-full h-full p-4 flex flex-col gap-3 min-h-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-slate-700 shrink-0">Рейтинг</span>
                    <Select
                        id="manual-ai-rating"
                        ariaLabel="Рейтинг отзыва"
                        options={RATING_OPTIONS}
                        value={rating}
                        onChange={onRatingChange}
                        placeholder="Рейт."
                        uiSize="compact"
                        className="w-24"
                    />
                    {hasValidRating && <Rating value={normalizedRating} />}
                    <span className="text-sm text-slate-700 shrink-0">Платформа</span>
                    <Select
                        id="manual-ai-platform"
                        ariaLabel="Платформа"
                        options={PLATFORM_OPTIONS}
                        value={selectedPlatform || undefined}
                        onChange={onPlatformChange}
                        placeholder="МП"
                        uiSize="compact"
                        className="w-28"
                    />
                </div>

                <div className="flex-1 min-h-0 flex flex-col gap-3">
                    <Textarea
                        id="manual-ai-review-text"
                        ariaLabel="Текст отзыва"
                        placeholder={'Укажите звёздный рейтинг и добавьте текст отзыва для ИИ ответа'}
                        className="flex-1 min-h-0 border border-slate-200"
                        value={reviewText}
                        setValue={onReviewTextChange}
                    />

                    {aiErrorText ? (
                        <div className="flex-1 min-h-0 border border-red-300 rounded-lg bg-red-50 p-4 text-sm leading-5 text-red-600 whitespace-pre-line overflow-auto">
                            {aiErrorText}
                        </div>
                    ) : (
                        <div className="flex min-h-0 flex-1 flex-col gap-1">
                            <Textarea
                                id="manual-ai-answer-text"
                                ariaLabel="Сгенерированный ответ"
                                placeholder={'Нажмите "ИИ-ответ"'}
                                className="flex-1 min-h-0 border border-slate-200"
                                value={aiText}
                                setValue={setAiText}
                                disabled={isAiGenerating}
                            />
                            <AnswerTemplateVariableHint padded />
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-3 gap-2">
                    <AnswerTemplatesControl {...templatesBindings} />
                    <QuotaActionButton
                        reason={aiAnswerDisabledReason}
                        isQuotaExhausted={isAiAnswerQuotaExhausted}
                        priorityType={UIPriority.SECONDARY}
                        disabled={!canGenerate}
                        onClick={generateAiAnswer}
                        className="h-8 w-full"
                        uiSize="compact"
                    >
                        {isAiGenerating ? 'Генерируем...' : 'ИИ-ответ'}
                    </QuotaActionButton>
                    <Button
                        priorityType={UIPriority.SECONDARY}
                        disabled={!aiText.trim()}
                        onClick={copyAiAnswer}
                        className="h-8"
                        uiSize="compact"
                    >
                        Скопировать
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ModalAiManual;
